// "Start a new spreadsheet" templates (the offline fallback): workbook JSON in the
// snapshot shape the previous editor saved, turned into engine operations by
// features/workbook/workbookJson.ts. Formula cells carry their computed value
// too. Kept free of editor imports so the home screen stays a light chunk.

import type { HomeTemplate } from "@/features/home/homeModel";

type Style = Record<string, string>;

interface Cell {
    value?: string | number;
    formula?: string;
    style?: Style;
    format?: string;
}

interface Row {
    cells: Cell[];
    height?: number;
}

const GREEN = "#107C41";
const MONEY = "#,##0.00";
const TITLE: Style = { fontWeight: "bold", fontSize: "16pt", color: GREEN };
const HEADER: Style = { fontWeight: "bold", backgroundColor: GREEN, color: "#FFFFFF", verticalAlign: "middle" };
const TOTAL: Style = { fontWeight: "bold", borderTop: "1px solid #107C41" };
const RIGHT: Style = { textAlign: "right" };

const text = (value: string, style?: Style): Cell => ({ value, style });
const money = (value: number, style?: Style): Cell => ({ value, format: MONEY, style });
const formula = (f: string, value: number, style?: Style): Cell => ({ formula: f, value, format: MONEY, style });
const header = (...labels: string[]): Row => ({ height: 26, cells: labels.map((l) => text(l, HEADER)) });
const blankRow = (): Row => ({ cells: [] });

function workbook(sheetName: string, columnWidths: number[], rows: Row[]): object {
    return {
        Workbook: {
            activeSheetIndex: 0,
            sheets: [
                {
                    name: sheetName,
                    columns: columnWidths.map((width) => ({ width })),
                    rows,
                    selectedRange: "A1:A1"
                }
            ]
        }
    };
}

function isoDate(offsetDays = 0): string {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return d.toISOString().slice(0, 10);
}

function budget(): object {
    const lines: Array<[string, number, number]> = [
        ["Housing", 1200, 1200],
        ["Groceries", 450, 482.3],
        ["Transport", 180, 150],
        ["Utilities", 220, 236.75],
        ["Savings", 500, 500],
        ["Entertainment", 150, 95.4]
    ];
    const first = 4; // Excel row of the first category (1-based)
    const last = first + lines.length - 1;
    const sum = (i: 1 | 2) => lines.reduce((acc, l) => acc + l[i], 0);
    return workbook("Budget", [180, 110, 110, 110], [
        { cells: [text("Monthly budget", TITLE)], height: 30 },
        blankRow(),
        header("Category", "Planned", "Actual", "Difference"),
        ...lines.map(([name, planned, actual], i) => ({
            cells: [
                text(name),
                money(planned),
                money(actual),
                formula(`=B${first + i}-C${first + i}`, planned - actual)
            ]
        })),
        {
            cells: [
                text("Total", TOTAL),
                formula(`=SUM(B${first}:B${last})`, sum(1), TOTAL),
                formula(`=SUM(C${first}:C${last})`, sum(2), TOTAL),
                formula(`=SUM(D${first}:D${last})`, sum(1) - sum(2), TOTAL)
            ]
        }
    ]);
}

function todo(): object {
    const tasks: Array<[string, string]> = [
        ["Plan the week", "High"],
        ["Reply to emails", "Medium"],
        ["Prepare the report", "High"],
        ["Book travel", "Low"],
        ["", ""],
        ["", ""],
        ["", ""],
        ["", ""]
    ];
    return workbook("To-do", [60, 260, 140, 110, 90, 220], [
        { cells: [text("To-do list", TITLE)], height: 30 },
        blankRow(),
        header("Done", "Task", "Owner", "Due date", "Priority", "Notes"),
        ...tasks.map(([task, priority]) => ({
            cells: [
                text("☐", { textAlign: "center" }),
                text(task),
                text(""),
                text(task ? isoDate(7) : ""),
                text(priority),
                text("")
            ]
        }))
    ]);
}

function invoice(): object {
    const items: Array<[string, number, number]> = [
        ["Consulting (hours)", 10, 85],
        ["Design work", 1, 450],
        ["Hosting (monthly)", 3, 25]
    ];
    const first = 10; // Excel row of the first line item
    const rowsForItems = 5; // leaves blank lines to fill in
    const last = first + rowsForItems - 1;
    const subtotal = items.reduce((acc, [, qty, price]) => acc + qty * price, 0);
    const tax = Math.round(subtotal * 0.1 * 100) / 100;
    const lineRows: Row[] = Array.from({ length: rowsForItems }, (_, i) => {
        const item = items[i];
        const r = first + i;
        return item
            ? { cells: [text(item[0]), { value: item[1] }, money(item[2]), formula(`=B${r}*C${r}`, item[1] * item[2])] }
            : { cells: [text(""), text(""), text(""), formula(`=B${r}*C${r}`, 0)] };
    });
    return workbook("Invoice", [260, 80, 110, 120], [
        { cells: [text("INVOICE", TITLE), text(""), text("Invoice #", RIGHT), text("INV-0001")], height: 30 },
        { cells: [text("Your Company", { fontWeight: "bold" }), text(""), text("Date", RIGHT), text(isoDate())] },
        { cells: [text("Street address, City"), text(""), text("Due", RIGHT), text(isoDate(30))] },
        blankRow(),
        { cells: [text("Bill to", { fontWeight: "bold", color: GREEN })] },
        { cells: [text("Client name")] },
        { cells: [text("Client address")] },
        blankRow(),
        header("Description", "Qty", "Unit price", "Amount"),
        ...lineRows,
        blankRow(),
        { cells: [text(""), text(""), text("Subtotal", RIGHT), formula(`=SUM(D${first}:D${last})`, subtotal)] },
        { cells: [text(""), text(""), text("Tax (10%)", RIGHT), formula(`=D${last + 2}*0.1`, tax)] },
        { cells: [text(""), text(""), text("Total", { ...TOTAL, textAlign: "right" }), formula(`=D${last + 2}+D${last + 3}`, subtotal + tax, TOTAL)] }
    ]);
}

interface SheetTemplate extends HomeTemplate {
    fileName: string;
    build: () => object;
}

export const SHEET_TEMPLATES: SheetTemplate[] = [
    {
        id: "budget",
        name: "Budget",
        description: "Planned vs actual with totals",
        fileName: "Budget.xlsx",
        preview: { accent: GREEN, blocks: ["title", "gap", "table", "text", "text", "text", "text", "short"] },
        build: budget
    },
    {
        id: "todo",
        name: "To-do list",
        description: "Tasks, owners, due dates",
        fileName: "To-do list.xlsx",
        preview: { accent: "#2B579A", blocks: ["title", "gap", "bullet", "bullet", "bullet", "bullet", "bullet", "bullet"] },
        build: todo
    },
    {
        id: "invoice",
        name: "Invoice",
        description: "Line items, tax and total",
        fileName: "Invoice.xlsx",
        preview: { accent: "#7F6000", blocks: ["title", "short", "short", "gap", "table", "text", "text", "gap", "short"] },
        build: invoice
    }
];

/** Workbook JSON + suggested file name for a template id, or null for an unknown id. */
export function buildSheetTemplate(id: string): { workbook: object; fileName: string } | null {
    const template = SHEET_TEMPLATES.find((t) => t.id === id);
    return template ? { workbook: template.build(), fileName: template.fileName } : null;
}
